import React from 'react';
import { motion } from 'framer-motion';
import Banner from '../components/Banner';
import ProductShowcase from '../components/ProductShowcase';
import { useMaterials } from '../hooks/useApi';
import { Link } from 'react-router-dom';
import { ArrowRight, Package } from 'lucide-react';
import analytics from '../utils/analytics';
import { usePageTitle } from '../hooks/usePageTitle';

const HomePage: React.FC = () => {
  // 设置页面标题和描述
  usePageTitle(
    '专业窑炉技术服务及设备制造',
    '佛山信力凯专注窑炉及加热技术解决方案，融合先进节能环保技术与智能化控制系统，为客户提供高效、绿色、智能的工业窑炉设备，助力企业增效降本。'
  );

  // 从静态 JSON 获取器件材料数据
  const { data: materials } = useMaterials({
    limit: 4
  });



  return (
    <div className="pt-16">
      {/* Hero Banner */}
      <Banner />



      {/* Product Showcase Section */}
      <section className="section-padding bg-gradient-primary">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              产品展示
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              提供多种工业炉及燃烧控制设备，满足客户不同产品、不同工况需求
            </p>
          </motion.div>
        </div>
      </section>
      <ProductShowcase showHeader={false} backgroundClass="bg-white" />

      {/* Materials Section Title */}
      <section className="section-padding bg-gradient-primary">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              器件材料
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              提供专业的窑炉器件及材料，满足不同工况需求
            </p>
          </motion.div>
        </div>
      </section>

      {/* Materials Content */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {materials.slice(0, 4).map((material, index: number) => (
              <motion.div
                key={material.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/materials/${material.id}`}
                  className="card group hover:scale-105 transition-all duration-300 block h-full flex flex-col"
                  onClick={() => analytics.material.clickCard(material.title)}
                >
                  <div className="relative overflow-hidden rounded-t-xl bg-gray-100">
                    <img
                      src={material.image || '/images/placeholder-product.jpg'}
                      alt={material.title}
                      className="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/images/placeholder-product.jpg';
                      }}
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                      {material.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600 mb-3">
                      <Package className="w-4 h-4 mr-1" />
                      <span>{material.title_en || '专业器件'}</span>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4 flex-1">
                      {material.description}
                    </p>
                    <div className="flex items-center text-primary-600 text-sm font-medium mt-auto">
                      <span>查看详情</span>
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <Link
              to="/materials"
              className="btn-primary"
            >
              查看所有器件
            </Link>
          </motion.div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="section-padding bg-gradient-primary">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              准备开始您的项目？
            </h2>
            <p className="text-xl text-white/90 mb-8">
              我们的专业团队随时准备为您提供最优质的窑炉技术解决方案
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary-600 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200"
              >
                立即咨询
              </Link>
              <Link
                to="/products"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-colors duration-200"
              >
                查看产品
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
